import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Input,
  HostListener,
  NgZone
} from '@angular/core';

export type NcDirectionType = 'right' | 'left' | 'up' | 'down' | 'diagonal';
export type NcShapeType = 'square' | 'hexagon' | 'triangle' | 'circle';

@Component({
  selector: 'nc-shape-grid-background',
  templateUrl: './shape-grid-background.component.html',
  styleUrls: ['./shape-grid-background.component.less']
})
export class ShapeGridBackgroundComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  /** 动画移动方向，可选值：right, left, up, down, diagonal */
  @Input() ncDirection: NcDirectionType = 'diagonal';
  /** 动画移动速度（0.1-2） */
  @Input() ncSpeed: number = 0.5;
  /** 网格边框颜色 */
  @Input() ncBorderColor: string = '#271e37';
  /** 形状大小（10-100） */
  @Input() ncSquareSize: number = 40;
  /** 悬停时的填充颜色 */
  @Input() ncHoverFillColor: string = '#222222';
  /** 形状类型，可选值：square, hexagon, triangle, circle */
  @Input() ncShape: NcShapeType = 'square';
  /** 鼠标悬停尾迹长度（0-20） */
  @Input() ncHoverTrailAmount: number = 0;
  /** 背景色 */
  @Input() ncBgColor: string = '#000000';

  private ctx: CanvasRenderingContext2D | null = null;
  private rafId: number | null = null;
  private resizeObserver!: ResizeObserver;


  private gridOffset = { x: 0, y: 0 };
  private hoveredSquare: { x: number; y: number } | null = null;
  private trailCells: { x: number; y: number }[] = [];
  private cellOpacities = new Map<string, number>();

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d');

    // 需要在zone外执行的代码用this.ngZone.runOutsideAngular，比如Canvas 更新
    this.ngZone.runOutsideAngular(() => {
      this.setupResizeObserver(canvas);
      this.resizeCanvas();
      this.updateAnimation();
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver(canvas: HTMLCanvasElement): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
    });
    this.resizeObserver.observe(canvas.parentElement || canvas);
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    }
  }

  private drawHex(cx: number, cy: number, size: number): void {
    if (!this.ctx) return;
    this.ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i;
      const vx = cx + size * Math.cos(angle);
      const vy = cy + size * Math.sin(angle);
      if (i === 0) this.ctx.moveTo(vx, vy);
      else this.ctx.lineTo(vx, vy);
    }
    this.ctx.closePath();
  }

  private drawCircle(cx: number, cy: number, size: number): void {
    if (!this.ctx) return;
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    this.ctx.closePath();
  }

  private drawTriangle(cx: number, cy: number, size: number, flip: boolean): void {
    if (!this.ctx) return;
    this.ctx.beginPath();
    if (flip) {
      this.ctx.moveTo(cx, cy + size / 2);
      this.ctx.lineTo(cx + size / 2, cy - size / 2);
      this.ctx.lineTo(cx - size / 2, cy - size / 2);
    } else {
      this.ctx.moveTo(cx, cy - size / 2);
      this.ctx.lineTo(cx + size / 2, cy + size / 2);
      this.ctx.lineTo(cx - size / 2, cy + size / 2);
    }
    this.ctx.closePath();
  }

  private drawGrid(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = this.ctx;
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const isHex = this.ncShape === 'hexagon';
    const isTri = this.ncShape === 'triangle';
    const hexHoriz = this.ncSquareSize * 1.5;
    const hexVert = this.ncSquareSize * Math.sqrt(3);

    if (isHex) {
      const colShift = Math.floor(this.gridOffset.x / hexHoriz);
      const offsetX = ((this.gridOffset.x % hexHoriz) + hexHoriz) % hexHoriz;
      const offsetY = ((this.gridOffset.y % hexVert) + hexVert) % hexVert;

      const cols = Math.ceil(canvas.width / hexHoriz) + 3;
      const rows = Math.ceil(canvas.height / hexVert) + 3;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const cx = col * hexHoriz + offsetX;
          const cy = row * hexVert + ((col + colShift) % 2 !== 0 ? hexVert / 2 : 0) + offsetY;

          const cellKey = `${col},${row}`;
          const alpha = this.cellOpacities.get(cellKey);
          if (alpha) {
            ctx.globalAlpha = alpha;
            this.drawHex(cx, cy, this.ncSquareSize);
            ctx.fillStyle = this.ncHoverFillColor;
            ctx.fill();
            ctx.globalAlpha = 1;
          }

          this.drawHex(cx, cy, this.ncSquareSize);
          ctx.strokeStyle = this.ncBorderColor;
          ctx.stroke();
        }
      }
    } else if (isTri) {
      const halfW = this.ncSquareSize / 2;
      const colShift = Math.floor(this.gridOffset.x / halfW);
      const rowShift = Math.floor(this.gridOffset.y / this.ncSquareSize);
      const offsetX = ((this.gridOffset.x % halfW) + halfW) % halfW;
      const offsetY = ((this.gridOffset.y % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;

      const cols = Math.ceil(canvas.width / halfW) + 4;
      const rows = Math.ceil(canvas.height / this.ncSquareSize) + 4;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const cx = col * halfW + offsetX;
          const cy = row * this.ncSquareSize + this.ncSquareSize / 2 + offsetY;
          const flip = ((col + colShift + row + rowShift) % 2 + 2) % 2 !== 0;

          const cellKey = `${col},${row}`;
          const alpha = this.cellOpacities.get(cellKey);
          if (alpha) {
            ctx.globalAlpha = alpha;
            this.drawTriangle(cx, cy, this.ncSquareSize, flip);
            ctx.fillStyle = this.ncHoverFillColor;
            ctx.fill();
            ctx.globalAlpha = 1;
          }

          this.drawTriangle(cx, cy, this.ncSquareSize, flip);
          ctx.strokeStyle = this.ncBorderColor;
          ctx.stroke();
        }
      }
    } else if (this.ncShape === 'circle') {
      const offsetX = ((this.gridOffset.x % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;
      const offsetY = ((this.gridOffset.y % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;

      const cols = Math.ceil(canvas.width / this.ncSquareSize) + 3;
      const rows = Math.ceil(canvas.height / this.ncSquareSize) + 3;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const cx = col * this.ncSquareSize + this.ncSquareSize / 2 + offsetX;
          const cy = row * this.ncSquareSize + this.ncSquareSize / 2 + offsetY;

          const cellKey = `${col},${row}`;
          const alpha = this.cellOpacities.get(cellKey);
          if (alpha) {
            ctx.globalAlpha = alpha;
            this.drawCircle(cx, cy, this.ncSquareSize);
            ctx.fillStyle = this.ncHoverFillColor;
            ctx.fill();
            ctx.globalAlpha = 1;
          }

          this.drawCircle(cx, cy, this.ncSquareSize);
          ctx.strokeStyle = this.ncBorderColor;
          ctx.stroke();
        }
      }
    } else {
      const offsetX = ((this.gridOffset.x % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;
      const offsetY = ((this.gridOffset.y % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;

      const cols = Math.ceil(canvas.width / this.ncSquareSize) + 3;
      const rows = Math.ceil(canvas.height / this.ncSquareSize) + 3;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const sx = col * this.ncSquareSize + offsetX;
          const sy = row * this.ncSquareSize + offsetY;

          const cellKey = `${col},${row}`;
          const alpha = this.cellOpacities.get(cellKey);
          if (alpha) {
            ctx.globalAlpha = alpha;
            ctx.fillStyle = this.ncHoverFillColor;
            ctx.fillRect(sx, sy, this.ncSquareSize, this.ncSquareSize);
            ctx.globalAlpha = 1;
          }

          ctx.strokeStyle = this.ncBorderColor;
          ctx.strokeRect(sx, sy, this.ncSquareSize, this.ncSquareSize);
        }
      }
    }

    const gradient = ctx.createRadialGradient(
      canvas.width / 2,
      canvas.height / 2,
      0,
      canvas.width / 2,
      canvas.height / 2,
      Math.sqrt(canvas.width ** 2 + canvas.height ** 2) / 2
    );
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  private updateAnimation(): void {
    const effectiveSpeed = Math.max(this.ncSpeed, 0.1);
    const isHex = this.ncShape === 'hexagon';
    const isTri = this.ncShape === 'triangle';
    const hexHoriz = this.ncSquareSize * 1.5;
    const hexVert = this.ncSquareSize * Math.sqrt(3);

    const wrapX = isHex ? hexHoriz * 2 : this.ncSquareSize;
    const wrapY = isHex ? hexVert : isTri ? this.ncSquareSize * 2 : this.ncSquareSize;

    switch (this.ncDirection) {
      case 'right':
        this.gridOffset.x = (this.gridOffset.x - effectiveSpeed + wrapX) % wrapX;
        break;
      case 'left':
        this.gridOffset.x = (this.gridOffset.x + effectiveSpeed + wrapX) % wrapX;
        break;
      case 'up':
        this.gridOffset.y = (this.gridOffset.y + effectiveSpeed + wrapY) % wrapY;
        break;
      case 'down':
        this.gridOffset.y = (this.gridOffset.y - effectiveSpeed + wrapY) % wrapY;
        break;
      case 'diagonal':
        this.gridOffset.x = (this.gridOffset.x - effectiveSpeed + wrapX) % wrapX;
        this.gridOffset.y = (this.gridOffset.y - effectiveSpeed + wrapY) % wrapY;
        break;
      default:
        break;
    }

    this.updateCellOpacities();
    this.drawGrid();

    // animation loop
    this.rafId = requestAnimationFrame(() => this.updateAnimation());
  }

  private updateCellOpacities(): void {
    const targets = new Map<string, number>();

    if (this.hoveredSquare) {
      targets.set(`${this.hoveredSquare.x},${this.hoveredSquare.y}`, 1);
    }

    if (this.ncHoverTrailAmount > 0) {
      for (let i = 0; i < this.trailCells.length; i++) {
        const t = this.trailCells[i];
        const key = `${t.x},${t.y}`;
        if (!targets.has(key)) {
          targets.set(key, (this.trailCells.length - i) / (this.trailCells.length + 1));
        }
      }
    }

    for (const [key] of targets) {
      if (!this.cellOpacities.has(key)) {
        this.cellOpacities.set(key, 0);
      }
    }

    for (const [key, opacity] of this.cellOpacities) {
      const target = targets.get(key) || 0;
      const next = opacity + (target - opacity) * 0.15;
      if (next < 0.005) {
        this.cellOpacities.delete(key);
      } else {
        this.cellOpacities.set(key, next);
      }
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const isHex = this.ncShape === 'hexagon';
    const isTri = this.ncShape === 'triangle';
    const hexHoriz = this.ncSquareSize * 1.5;
    const hexVert = this.ncSquareSize * Math.sqrt(3);

    let col = 0;
    let row = 0;

    if (isHex) {
      const colShift = Math.floor(this.gridOffset.x / hexHoriz);
      const offsetX = ((this.gridOffset.x % hexHoriz) + hexHoriz) % hexHoriz;
      const offsetY = ((this.gridOffset.y % hexVert) + hexVert) % hexVert;
      const adjustedX = mouseX - offsetX;
      const adjustedY = mouseY - offsetY;

      col = Math.round(adjustedX / hexHoriz);
      const rowOffset = (col + colShift) % 2 !== 0 ? hexVert / 2 : 0;
      row = Math.round((adjustedY - rowOffset) / hexVert);
    } else if (isTri) {
      const halfW = this.ncSquareSize / 2;
      const offsetX = ((this.gridOffset.x % halfW) + halfW) % halfW;
      const offsetY = ((this.gridOffset.y % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;

      const adjustedX = mouseX - offsetX;
      const adjustedY = mouseY - offsetY;

      col = Math.round(adjustedX / halfW);
      row = Math.floor(adjustedY / this.ncSquareSize);
    } else if (this.ncShape === 'circle') {
      const offsetX = ((this.gridOffset.x % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;
      const offsetY = ((this.gridOffset.y % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;

      const adjustedX = mouseX - offsetX;
      const adjustedY = mouseY - offsetY;

      col = Math.round(adjustedX / this.ncSquareSize);
      row = Math.round(adjustedY / this.ncSquareSize);
    } else {
      const offsetX = ((this.gridOffset.x % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;
      const offsetY = ((this.gridOffset.y % this.ncSquareSize) + this.ncSquareSize) % this.ncSquareSize;

      const adjustedX = mouseX - offsetX;
      const adjustedY = mouseY - offsetY;

      col = Math.floor(adjustedX / this.ncSquareSize);
      row = Math.floor(adjustedY / this.ncSquareSize);
    }

    if (!this.hoveredSquare || this.hoveredSquare.x !== col || this.hoveredSquare.y !== row) {
      if (this.hoveredSquare && this.ncHoverTrailAmount > 0) {
        this.trailCells.unshift({ ...this.hoveredSquare });
        if (this.trailCells.length > this.ncHoverTrailAmount) {
          this.trailCells.length = this.ncHoverTrailAmount;
        }
      }
      this.hoveredSquare = { x: col, y: row };
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.hoveredSquare && this.ncHoverTrailAmount > 0) {
      this.trailCells.unshift({ ...this.hoveredSquare });
      if (this.trailCells.length > this.ncHoverTrailAmount) {
        this.trailCells.length = this.ncHoverTrailAmount;
      }
    }
    this.hoveredSquare = null;
  }
}
